import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deletePackagingDamageDispatch,
  getPackagingDamageDispatches,
  removePackagingDamageDispatch,
} from 'src/redux/slices/packaging-damage-dispatch';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  IPackagingDamageDispatchListItem,
  IPackagingDamageDispatchTableFilters,
  IPackagingDamageDispatchTableFilterValue,
  IPackagingDamageDispatchTableSort,
} from 'src/types/packaging-damage-dispatch';

import PackagingDamageDispatchTableRow from '../packaging-damage-dispatch-table-row';
import PackagingDamageDispatchTableToolbar from '../packaging-damage-dispatch-table-toolbar';

const TABLE_HEAD = [
  { id: 'damageCode', label: 'Damage #', align: 'left' },
  { id: 'damageDate', label: 'Damage Date', align: 'left' },
  { id: 'remarks', label: 'Remarks', align: 'left' },
  { id: '' },
];

const defaultFilters: IPackagingDamageDispatchTableFilters = {
  generalSearch: '',
  startDate: null,
  endDate: null,
};

// Sorted by createdAt (not updatedAt) so the top row on an unfiltered first
// page always matches the backend's "most recently created" rule — that's
// the only row delete is enabled for.
const defaultSortBy: IPackagingDamageDispatchTableSort = {
  sortBy: 'createdAt',
  order: 'desc',
};

export default function PackagingDamageDispatchListView() {
  const { status, damageDispatches, error } = useAppSelector(
    (state) => state.packagingDamageDispatch
  );

  const { user: authUser } = useAuthContext();
  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  const canDetermineLastDamageDispatch =
    page === 0 &&
    sort.sortBy === 'createdAt' &&
    sort.order === 'desc' &&
    !filters.generalSearch &&
    !filters.startDate &&
    !filters.endDate;

  const handleFilters = useCallback(
    (name: string, value: IPackagingDamageDispatchTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const fetchPaginatedDamageDispatches = useCallback(() => {
    dispatch(
      getPackagingDamageDispatches({
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        startDate: filters.startDate,
        endDate: filters.endDate,
        sort: sort.order,
        sortBy: sort.sortBy,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  const handleDeleteRow = useCallback(
    async (item: IPackagingDamageDispatchListItem, index: number) => {
      dispatch(removePackagingDamageDispatch({ damageDispatchId: item._id }));

      try {
        await dispatch(deletePackagingDamageDispatch({ item, index })).unwrap();

        fetchPaginatedDamageDispatches();
        enqueueSnackbar('Damage dispatch deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedDamageDispatches]
  );

  useEffect(() => {
    fetchPaginatedDamageDispatches();
  }, [fetchPaginatedDamageDispatches]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Packaging Damage Dispatch"
        links={[]}
        action={
          <>
            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_PACKAGING_DAMAGE_DISPATCH,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.packagingDamageDispatch.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Damage Dispatch
              </Button>
            )}
          </>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <PackagingDamageDispatchTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 760 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD}
                rowCount={damageDispatches.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {damageDispatches?.items.map(
                      (row: IPackagingDamageDispatchListItem, index: number) => (
                        <PackagingDamageDispatchTableRow
                          key={row._id}
                          row={row}
                          isEditable={canDetermineLastDamageDispatch && index === 0}
                          onDeleteRow={() => handleDeleteRow(row, index)}
                        />
                      )
                    )}
                  </>
                )}

                {status !== 'failure' && damageDispatches.count === 0 && (
                  <TableNoData
                    message="No damage dispatches found"
                    notFound={damageDispatches.count === 0}
                  />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={damageDispatches.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
